import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { createMlbSearchUrl, fetchMlbContentHub } from '../../api/mlbContent';
import ContentCard from './ContentCard';
import ContentFilters from './ContentFilters';
import ContentSkeleton from './ContentSkeleton';

const { FiArrowLeft, FiFilm, FiRefreshCw, FiExternalLink, FiInbox } = FiIcons;

const MlbContentPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], players: [], games: [] });
  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');

    try {
      setData(await fetchMlbContentHub(111, 12));
    } catch (err) {
      setError(err.message || 'MLB content could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    document.title = 'Red Sox News & Video Highlights | Green Monster';
  }, []);

  const results = useMemo(() => {
    const search = query.trim().toLowerCase();

    return data.items.filter((item) => {
      const matchesType = type === 'all' || item.type === type;
      const matchesSearch = !search || item.searchText.includes(search);
      return matchesType && matchesSearch;
    });
  }, [data.items, query, type]);

  const relatedPlayers = (item) => data.players.filter(
    (player) => item.searchText.includes(player.name.toLowerCase())
  ).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#1e3a29] p-3 sm:p-6 md:p-10">
      <main className="mx-auto flex max-w-6xl flex-col gap-5">
        <header className="flex items-center justify-between gap-3 border-b-2 border-[#658d71] pb-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              aria-label="Back to scoreboard"
              className="border border-[#658d71] p-2 text-[#d8ceb3] transition-colors hover:bg-[#386646]"
            >
              <SafeIcon icon={FiArrowLeft} size={18} />
            </button>
            <div>
              <h1 className="flex items-center gap-2 text-xl font-black uppercase tracking-widest text-[#d8ceb3] sm:text-3xl">
                <SafeIcon icon={FiFilm} size={22} />
                News & Highlights
              </h1>
              <p className="mt-1 text-[9px] font-bold uppercase tracking-widest text-[#658d71]">
                Recent games, official MLB videos, stories, and player connections
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 border border-[#658d71] px-3 py-2 text-[9px] font-black uppercase tracking-widest text-[#a9c9b1] transition-colors hover:border-[#a9c9b1] hover:text-white disabled:opacity-50"
          >
            <SafeIcon icon={FiRefreshCw} size={12} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </header>

        <section className="border-2 border-[#658d71] bg-[rgba(0,0,0,0.08)]">
          <div className="border-b border-[#658d71] bg-[#386646] p-3 sm:p-4">
            <ContentFilters
              query={query}
              type={type}
              onQueryChange={setQuery}
              onTypeChange={setType}
            />
          </div>

          <div className="p-3 sm:p-5">
            {loading && <ContentSkeleton />}

            {!loading && error && (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <SafeIcon icon={FiInbox} size={30} className="text-[#658d71]" />
                <p className="text-xs font-bold uppercase tracking-widest text-[#e85555]">
                  {error}
                </p>
                <button
                  type="button"
                  onClick={load}
                  className="border border-[#658d71] px-4 py-2 text-[9px] font-black uppercase tracking-widest text-[#a9c9b1] hover:text-white"
                >
                  Try Again
                </button>
              </div>
            )}

            {!loading && !error && results.length === 0 && (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <SafeIcon icon={FiInbox} size={30} className="text-[#658d71]" />
                <p className="text-sm font-black uppercase tracking-widest text-[#d8ceb3]">
                  No matching MLB content
                </p>
                <p className="max-w-sm text-[10px] leading-relaxed text-[#a9c9b1]">
                  Try another player, opponent, game, or content type. You can also continue
                  your search on the official MLB website.
                </p>
                <a
                  href={createMlbSearchUrl(query)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 border border-[#d8ceb3] px-4 py-2 text-[9px] font-black uppercase tracking-widest text-[#d8ceb3] hover:bg-[#d8ceb3] hover:text-[#1e3a29]"
                >
                  Search MLB
                  <SafeIcon icon={FiExternalLink} size={11} />
                </a>
              </div>
            )}

            {!loading && !error && results.length > 0 && (
              <>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <span className="text-[9px] font-bold uppercase tracking-widest text-[#658d71]">
                    {results.length} result{results.length === 1 ? '' : 's'}
                  </span>
                  <span className="text-[8px] font-bold uppercase tracking-widest text-[#658d71]">
                    Official MLB sources
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {results.map((item) => (
                    <ContentCard
                      key={item.id}
                      item={item}
                      matchedPlayers={relatedPlayers(item)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default MlbContentPage;